import React, { useState, useRef, useContext } from 'react';
import { useTranslation } from "react-i18next";
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { Tooltip } from 'primereact/tooltip';
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog';
import CustomDropdown from '../../../../custom/customDropdown';
import { AuthContext } from '../../../../Header/auth - context';
import { useSelector } from 'react-redux';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

const mesecOptions = [
    { value: 1, label: 'Januar' }, { value: 2, label: 'Februar' },
    { value: 3, label: 'Mart' }, { value: 4, label: 'April' },
    { value: 5, label: 'Maj' }, { value: 6, label: 'Jun' },
    { value: 7, label: 'Jul' }, { value: 8, label: 'Avgust' },
    { value: 9, label: 'September' }, { value: 10, label: 'Oktobar' },
    { value: 11, label: 'Novembar' }, { value: 12, label: 'Decembar' },
];


const purposeCodeOptions = [
    { value: 240, label: '240 - Zarade zaposlenih' },
    { value: 249, label: '249 - Isplata fizičkom licu van R.O.' },
    { value: 254, label: '254 - Porezi i doprinosi' },
];

const MESEC_MAP = {
    'JANUAR': 1, 'FEBRUAR': 2, 'MART': 3, 'APRIL': 4, 'MAJ': 5, 'JUN': 6,
    'JUL': 7, 'AVGUST': 8, 'SEPTEMBAR': 9, 'OKTOBAR': 10, 'NOVEMBAR': 11, 'DECEMBAR': 12,
};

const parsePurpose = (purpose) => {
    const words = (purpose || '').toUpperCase().split(/\s+/);
    let mesec = null;
    let godina = null;
    for (const word of words) {
        const clean = word.replace(/[^A-ZŠĐČĆŽ]/g, '');
        if (MESEC_MAP[clean]) mesec = MESEC_MAP[clean];
        if (/^20\d{2}$/.test(word)) godina = parseInt(word);
    }
    return { mesec, godina };
};

const stripDashes = (val) => (val || '').replace(/-/g, '');

const formatRacun = (val) => {
    if (!val) return '-';
    const s = stripDashes(String(val));
    if (s.length <= 5) return s;
    return s.slice(0, 3) + '-' + s.slice(3, s.length - 2) + '-' + s.slice(-2);
};

const emptyForm = {
    ID_zaposleni: null,
    ImePrezime: '',
    TekuciRacun: '',
    Iznos: null,
    ValutaMZ: null,
    Mesec: null,
    Godina: new Date().getFullYear(),
    Svrha: '',
    PurposeCode: 240,
};

const MinimalnaZaradaTab = ({ minimalnaZaradaDB, zaposleniDB, options, onRefresh, toast }) => {
  const { t } = useTranslation();
    const auth = useContext(AuthContext);
    const company = useSelector(state => state.company);
    const xmlInputRef = useRef(null);
    const [formData, setFormData] = useState(emptyForm);
    const [selectedId, setSelectedId] = useState(null);
    const [formSubmitted, setFormSubmitted] = useState(false);
    const [globalFilter, setGlobalFilter] = useState(null);

    const zaposleniOptions = (zaposleniDB || []).map(z => ({ value: z.id, label: z.NazivZaposlenog }));

    const resetForm = () => { setFormData(emptyForm); setSelectedId(null); setFormSubmitted(false); };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: name === 'TekuciRacun' ? stripDashes(value) : value }));
    };

    const handleZaposleniChange = (e) => {
        setFormData(prev => ({ ...prev, ID_zaposleni: e.target.value }));
    };

    const handleRowSelect = (row) => {
        const id = row?.id;
        setSelectedId(prev => {
            const next = String(prev) === String(id) ? null : id;
            if (next !== null) {
                setFormData({
                    ID_zaposleni: row.ID_zaposleni != null ? Number(row.ID_zaposleni) : null,
                    ImePrezime: row.ImePrezime || '',
                    TekuciRacun: stripDashes(row.TekuciRacun || ''),
                    Iznos: row.Iznos != null ? Number(row.Iznos) : null,
                    ValutaMZ: row.Valuta != null ? Number(row.Valuta) : null,
                    Mesec: row.Mesec != null ? Number(row.Mesec) : null,
                    Godina: row.Godina != null ? Number(row.Godina) : new Date().getFullYear(),
                    Svrha: row.Svrha || '',
                    PurposeCode: row.PurposeCode != null ? Number(row.PurposeCode) : 240,
                });
            } else {
                setFormData(emptyForm);
                setFormSubmitted(false);
            }
            return next;
        });
    };

    const handleSave = (e) => {
        e.preventDefault();
        setFormSubmitted(true);
        const { ID_zaposleni, Iznos, Mesec, Godina } = formData;
        if (!ID_zaposleni || Iznos == null || Mesec == null || !Godina) {
            toast.current.show({ severity: 'warn', summary: t('admin.m_popunite_obavezna_polja'), life: 3000 });
            return;
        }
        confirmDialog({
            message: selectedId ? 'Sačuvati izmene?' : 'Dodati novi zapis?',
            acceptLabel: 'Da', rejectLabel: 'Ne',
            icon: 'pi pi-info-circle', acceptClassName: 'p-button-danger',
            accept: () => doSave(),
        });
    };

    const doSave = () => {
        fetch(process.env.REACT_APP_BACKEND_URL + '/trip/ManageObracunSifarnik', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + auth.token },
            body: JSON.stringify({ step: 'SAVE_MINIMALNA', id: selectedId || 0, formData }),
        })
            .then(async r => {
                const data = await r.json();
                if (!r.ok) throw new Error(data?.message || 'Greška!');
                return data;
            })
            .then(data => {
                const msg = data[0]?.message || '';
                if (msg.includes('Nema izmena')) {
                    toast.current.show({ severity: 'warn', summary: msg, life: 3000 });
                    return;
                }
                toast.current.show({ severity: 'success', summary: msg || 'Uspešno!', life: 3000 });
                resetForm();
                onRefresh();
            })
            .catch(err => toast.current.show({ severity: 'error', summary: err.message || 'Greška pri čuvanju!', life: 3000 }));
    };

    const handleDelete = () => {
        if (!selectedId) { toast.current.show({ severity: 'warn', summary: t('admin.m_izaberite_zapis_za_brisanje'), life: 3000 }); return; }
        confirmDialog({
            message: 'Obrisati izabrani zapis?',
            acceptLabel: 'Da', rejectLabel: 'Ne',
            icon: 'pi pi-info-circle', acceptClassName: 'p-button-danger',
            accept: () => {
                fetch(process.env.REACT_APP_BACKEND_URL + '/trip/ManageObracunSifarnik', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + auth.token },
                    body: JSON.stringify({ step: 'DELETE_MINIMALNA', id: selectedId }),
                })
                    .then(r => r.json())
                    .then(() => { toast.current.show({ severity: 'success', summary: t('admin.m_zapis_obrisan'), life: 3000 }); resetForm(); onRefresh(); })
                    .catch(() => toast.current.show({ severity: 'error', summary: t('admin.m_greska_pri_brisanju'), life: 3000 }));
            },
        });
    };

    const handleXmlFile = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (evt) => {
            try {
                const parser = new DOMParser();
                const doc = parser.parseFromString(evt.target.result, 'text/xml');

                const rootTag = doc.documentElement?.tagName?.toLowerCase();
                if (rootTag !== 'pmtorderrq') {
                    e.target.value = '';
                    toast.current.show({ severity: 'error', summary: t('admin.m_neispravan_xml_potreban_je_xml_generisan'), life: 6000 });
                    return;
                }

                const orders = Array.from(doc.querySelectorAll('pmtorder'));
                const items = orders
                    .filter(o => o.querySelector('purposecode')?.textContent.trim() === '240')
                    .map(o => {
                        const svrha = (o.querySelector('purpose')?.textContent.trim() || '').substring(0, 500);
                        const { mesec, godina } = parsePurpose(svrha);
                        return {
                            ImePrezime: (o.querySelector('payeecompanyinfo name')?.textContent.trim() || '').substring(0, 200),
                            TekuciRacun: stripDashes(o.querySelector('payeeaccountinfo acctid')?.textContent.trim() || ''),
                            Iznos: parseFloat(o.querySelector('trnamt')?.textContent.trim() || '0'),
                            Valuta: o.querySelector('curdef')?.textContent.trim() || 'RSD',
                            Svrha: svrha,
                            PurposeCode: 240,
                            Mesec: mesec,
                            Godina: godina,
                        };
                    });

                e.target.value = '';

                if (items.length === 0) {
                    toast.current.show({ severity: 'warn', summary: t('admin.m_xml_ne_sadrzi_zapise_sa_purposecode_240'), life: 4000 });
                    return;
                }

                const noMesec = items.filter(i => !i.Mesec || !i.Godina).length;
                const info = noMesec > 0 ? ` (${noMesec} bez prepoznatog meseca)` : '';
                confirmDialog({
                    message: `Pronađeno ${items.length} zapisa zarada${info}. Uvesti sve?`,
                    acceptLabel: 'Da', rejectLabel: 'Ne',
                    icon: 'pi pi-upload', acceptClassName: 'p-button-success',
                    accept: () => doXmlImport(items),
                });
            } catch (err) {
                e.target.value = '';
                toast.current.show({ severity: 'error', summary: t('admin.m_greska_pri_citanju_xml_fajla'), life: 3000 });
            }
        };
        reader.readAsText(file, 'UTF-8');
    };

    const doXmlImport = (items) => {
        fetch(process.env.REACT_APP_BACKEND_URL + '/trip/ManageObracunSifarnik', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + auth.token },
            body: JSON.stringify({ step: 'SAVE_XML', id: 0, formData: { items } }),
        })
            .then(async r => {
                const data = await r.json();
                if (!r.ok) throw new Error(data?.message || 'Greška!');
                return data;
            })
            .then(data => {
                const { message, Inserted, Skipped, ID } = data[0] || {};
                const inserted = Number(Inserted ?? ID ?? 0);
                const severity = inserted === 0 ? 'warn' : Skipped > 0 ? 'warn' : 'success';
                toast.current.show({ severity, summary: message || 'XML uvoz završen.', life: 5000 });
                if (inserted > 0) onRefresh();
            })
            .catch(err => toast.current.show({ severity: 'error', summary: err.message || 'Greška pri uvozu XML!', life: 3000 }));
    };

    const exportExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Minimalna zarada');
        const headers = ['Zaposleni', 'Ime i prezime (XML)', 'Račun', 'Iznos', 'Valuta', 'Mesec', 'Godina', 'Svrha', 'Šifra svrhe'];
        const headerRow = worksheet.addRow(headers);
        headerRow.font = { bold: true };
        headerRow.eachCell(cell => {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEFEFEF' } };
            cell.border = { top: { style: 'medium' }, left: { style: 'medium' }, bottom: { style: 'medium' }, right: { style: 'medium' } };
        });
        (minimalnaZaradaDB || []).forEach(r => {
            const mesecLabel = mesecOptions.find(m => m.value === r.Mesec)?.label || r.Mesec || '';
            const row = worksheet.addRow([
                r.ZaposleniNaziv || '',
                r.ImePrezime || '',
                formatRacun(r.TekuciRacun),
                r.Iznos != null ? Number(r.Iznos) : '',
                r.ValutaNaziv || '',
                mesecLabel,
                r.Godina || '',
                r.Svrha || '',
                r.PurposeCode || '',
            ]);
            row.eachCell(cell => {
                cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
            });
        });
        worksheet.columns.forEach(col => {
            let max = 10;
            col.eachCell(cell => { if (cell.value) max = Math.max(max, String(cell.value).length); });
            col.width = max + 2;
        });
        const dt = new Date().toLocaleString('sr-RS', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Belgrade' }).replace(/,/g, '');
        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), `Minimalna zarada - ${company?.companyName || ''} - ${dt}.xlsx`);
    };

    const invalid = (val) => formSubmitted && (val === null || val === '' || val === undefined);

    const tableHeader = (
        <div className="export-buttons fakture-stavke-header">
            <Tooltip target=".export-buttons>button" position="bottom" />
            <div className="fakture-stavke-radnje">
                <Button type="button" icon="pi pi-file-excel" severity="success" rounded onClick={exportExcel} data-pr-tooltip="XLS" />
                <Button type="button" icon="pi pi-upload" severity="info" rounded onClick={() => xmlInputRef.current.click()} data-pr-tooltip={t('admin.uveziXml')} />
                {selectedId && <Button type="button" icon="pi pi-trash" severity="danger" rounded onClick={handleDelete} data-pr-tooltip={t('common.delete')} />}
            </div>
            <input type="search" placeholder={t('common.search')} className="p-inputtext p-component"
                onInput={e => setGlobalFilter(e.target.value)} />
        </div>
    );

    return (
        <div style={{ marginTop: '1%' }}>
            <Tooltip target=".field-tooltip" position="top" />
            <ConfirmDialog />
            <input type="file" accept=".xml" ref={xmlInputRef} style={{ display: 'none' }} onChange={handleXmlFile} />
            <div className="blagajnaDashboard dnevniceDashboard">
                <Card>
                    <form autoComplete="off">
                        <div className="gridLicni">
                            <div className="col-12 md:col-6" style={{ display: 'flex', flexDirection: 'column' }}>
                                <label>{t('finansije.zaposleni')}:</label>
                                <CustomDropdown name="ID_zaposleni" value={formData.ID_zaposleni}
                                    options={zaposleniOptions} optionLabel="label" placeholder={t('finansije.izaberiZaposlenog')}
                                    filter showClear
                                    required invalid={invalid(formData.ID_zaposleni)}
                                    onChange={handleZaposleniChange} />
                            </div>
                            <div className="col-12 md:col-6" style={{ display: 'flex', flexDirection: 'column' }}>
                                <label>{t('admin.racun')}:</label>
                                <InputText name="TekuciRacun" value={formData.TekuciRacun} onChange={handleChange} />
                            </div>
                            <div className="col-12 md:col-3" style={{ display: 'flex', flexDirection: 'column' }}>
                                <label>{t('nova.iznos')}:</label>
                                <InputNumber name="Iznos" value={formData.Iznos}
                                    onValueChange={handleChange} minFractionDigits={2} maxFractionDigits={2}
                                    required className={invalid(formData.Iznos) ? 'p-invalid' : ''} />
                            </div>
                            <div className="col-12 md:col-3" style={{ display: 'flex', flexDirection: 'column' }}>
                                <label>{t('nova.valuta')}:</label>
                                <CustomDropdown name="ValutaMZ" value={formData.ValutaMZ}
                                    options={options.ValuteDB} optionLabel="label" placeholder={t('nova.valuta')}
                                    onChange={handleChange} />
                            </div>
                            <div className="col-12 md:col-3" style={{ display: 'flex', flexDirection: 'column' }}>
                                <label>{t('finansije.mesec')}:</label>
                                <CustomDropdown name="Mesec" value={formData.Mesec}
                                    options={mesecOptions} optionLabel="label" placeholder={t('nova.phMesec')}
                                    required invalid={invalid(formData.Mesec)} onChange={handleChange} />
                            </div>
                            <div className="col-12 md:col-3" style={{ display: 'flex', flexDirection: 'column' }}>
                                <label>{t('finansije.godina')}:</label>
                                <InputNumber name="Godina" value={formData.Godina}
                                    onValueChange={handleChange} useGrouping={false}
                                    required className={invalid(formData.Godina) ? 'p-invalid' : ''} />
                            </div>
                            <div className="col-12 md:col-6" style={{ display: 'flex', flexDirection: 'column' }}>
                                <label>{t('admin.svrha')}:</label>
                                <InputText name="Svrha" value={formData.Svrha} onChange={handleChange} />
                            </div>
                            <div className="col-12 md:col-3" style={{ display: 'flex', flexDirection: 'column' }}>
                                <label>{t('admin.sifraSvrhe')}:</label>
                                <CustomDropdown name="PurposeCode" value={formData.PurposeCode}
                                    options={purposeCodeOptions} optionLabel="label" placeholder={t('admin.sifra')}
                                    onChange={handleChange} />
                            </div>
                        </div>
                        <div className="formUnosZap buttonUnosZap">
                            <button type="submit" onClick={handleSave} style={{ minWidth: 'fit-content', marginTop: '2%' }} className="btn btn-success">
                                {selectedId ? 'SAČUVAJ' : 'UNOS'}
                            </button>
                        </div>
                    </form>
                </Card>
                <Card>
                    <DataTable className="stavke-tabela" value={minimalnaZaradaDB || []} paginator rows={10} scrollable scrollHeight="45vh"
                        header={tableHeader} emptyMessage="Nema zapisa minimalne zarade" resizableColumns
                        globalFilter={globalFilter}
                        rowClassName={row => String(row.id) === String(selectedId) ? 'selected-row' : ''}>
                        <Column header={t('common.choose')} style={{ minWidth: '90px' }}
                            body={rowData => (
                                <Button label={t('common.choose')} icon="pi pi-eye" type="button"
                                    className="p-button-text p-button-sm" onClick={() => handleRowSelect(rowData)} />
                            )} />
                        <Column field="ZaposleniNaziv" header={t('finansije.zaposleni')} sortable style={{ minWidth: '160px' }}
                            body={r => r.ZaposleniNaziv || <span style={{ color: '#e57373', fontSize: '0.85em' }}>nije pronađen</span>} />
                        <Column field="ImePrezime" header={t('admin.imeXml')} sortable style={{ minWidth: '160px' }} />
                        <Column field="TekuciRacun" header={t('admin.racun')} style={{ minWidth: '160px' }} body={r => formatRacun(r.TekuciRacun)} />
                        <Column field="Iznos" header={t('nova.iznos')} sortable style={{ minWidth: '110px' }}
                            body={r => r.Iznos != null ? `${Number(r.Iznos).toFixed(2)} ${r.ValutaNaziv || ''}` : '-'} />
                        <Column field="Mesec" header={t('finansije.mesec')} sortable style={{ minWidth: '100px' }}
                            body={r => mesecOptions.find(m => m.value === r.Mesec)?.label || r.Mesec || '-'} />
                        <Column field="Godina" header={t('finansije.godina')} sortable style={{ minWidth: '80px' }} />
                        <Column field="PurposeCode" header={t('admin.sifra')} style={{ minWidth: '70px' }} body={r => r.PurposeCode || '-'} />
                        <Column field="Svrha" header={t('admin.svrha')} style={{ minWidth: '200px' }} body={r => r.Svrha || '-'} />
                    </DataTable>
                </Card>
            </div>
        </div>
    );
};

export default MinimalnaZaradaTab;
